/**
 * Product Benefits
 * - Desktop (min-width: 992px): hover [data-product-benefits-item] → active + synced images
 * - Mobile (< 992px): Swiper on .product-benefits-mobile-slider
 */
(function () {
  const SECTION_SELECTOR = '[data-product-benefits-section]';
  const ITEM_SELECTOR = '[data-product-benefits-item]';
  const LIST_SELECTOR = '.product-benefits-mobile-slider';
  const IMAGES_LIST_SELECTOR = '[data-benefits-images-list]';
  const ACTIVE_CLASS = 'active';
  const DESKTOP_MQ = '(min-width: 768px)';

  const desktopMedia = window.matchMedia(DESKTOP_MQ);

  /**
   * @returns {boolean}
   */
  function isDesktop() {
    return desktopMedia.matches;
  }

  /**
   * Sync [data-benefits-images-list] children active state by matching data-id.
   * @param {HTMLElement} section
   * @param {string} benefitId
   */
  function syncImagesById(section, benefitId) {
    const imagesList = section.querySelector(IMAGES_LIST_SELECTOR);
    if (!imagesList) return;

    imagesList.querySelectorAll('[data-id]').forEach((image) => {
      image.classList.toggle(ACTIVE_CLASS, image.getAttribute('data-id') === benefitId);
    });
  }

  /**
   * @param {HTMLElement} section
   * @param {HTMLElement} activeItem
   */
  function activateItem(section, activeItem) {
    if (!activeItem?.id) return;

    const benefitId = activeItem.id;

    section.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      item.classList.toggle(ACTIVE_CLASS, item.id === benefitId);
    });

    syncImagesById(section, benefitId);
  }

  /**
   * @param {HTMLElement} section
   * @param {NodeListOf<HTMLElement>|HTMLElement[]} items
   */
  function setInitialActive(section, items) {
    if (!items.length) return;
    activateItem(section, items[0]);
  }

  /**
   * Desktop: hover [data-product-benefits-item] to set active + sync images.
   * @param {HTMLElement} section
   */
  function bindDesktopHover(section) {
    unbindDesktopHover(section);

    if (!isDesktop()) return;

    const items = section.querySelectorAll(ITEM_SELECTOR);
    if (!items.length) return;

    const onMouseEnter = (event) => {
      if (!isDesktop()) return;
      activateItem(section, event.currentTarget);
    };

    items.forEach((item) => {
      item.addEventListener('mouseenter', onMouseEnter);
    });

    section._productBenefitsHoverHandler = onMouseEnter;
    section._productBenefitsHoverItems = items;
  }

  /**
   * @param {HTMLElement} section
   */
  function unbindDesktopHover(section) {
    const handler = section._productBenefitsHoverHandler;
    const items = section._productBenefitsHoverItems;

    if (handler && items?.length) {
      items.forEach((item) => {
        item.removeEventListener('mouseenter', handler);
      });
    }

    section._productBenefitsHoverHandler = null;
    section._productBenefitsHoverItems = null;
  }

  /**
   * On slide change: active slide → [data-product-benefits-item] id →
   * toggle active on [data-benefits-images-list] children by data-id.
   * @param {HTMLElement} section
   */
  function bindMobileSlider(section) {
    const swiperEl = section.querySelector(LIST_SELECTOR);
    if (!swiperEl || swiperEl.dataset.productBenefitsSlideBound === 'true') return;

    swiperEl.dataset.productBenefitsSlideBound = 'true';

    const onSlideChange = (event) => {
      if (isDesktop()) return;

      const swiper = event.detail?.[0] || swiperEl.swiper;
      if (!swiper) return;

      const activeSlide = swiper.slides?.[swiper.activeIndex];
      if (!activeSlide) return;

      const benefitItem = activeSlide.querySelector(ITEM_SELECTOR);
      if (!benefitItem?.id) return;

      activateItem(section, benefitItem);
    };

    swiperEl.addEventListener('swiperslidechange', onSlideChange);
    section._productBenefitsSlideHandler = onSlideChange;
    section._productBenefitsSwiperEl = swiperEl;
  }

  /**
   * @param {HTMLElement} section
   */
  function initSection(section) {
    if (!section) return;

    unbindDesktopHover(section);
    bindMobileSlider(section);

    const items = section.querySelectorAll(ITEM_SELECTOR);
    if (!items.length) return;

    setInitialActive(section, items);

    if (!isDesktop()) {
      section.dataset.productBenefitsInit = 'false';
      return;
    }

    section.dataset.productBenefitsInit = 'true';
    bindDesktopHover(section);
  }

  /**
   * @param {ParentNode} [root=document]
   */
  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const sections =
      scope.matches && scope.matches(SECTION_SELECTOR)
        ? [scope]
        : scope.querySelectorAll(SECTION_SELECTOR);

    sections.forEach(initSection);
  }

  function onSectionLoad(event) {
    const sectionEl = event.target;
    if (!sectionEl) return;

    const benefitsSection = sectionEl.matches?.(SECTION_SELECTOR)
      ? sectionEl
      : sectionEl.querySelector?.(SECTION_SELECTOR);

    if (!benefitsSection) return;

    benefitsSection.dataset.productBenefitsInit = 'false';

    const swiperEl = benefitsSection._productBenefitsSwiperEl;
    if (swiperEl && benefitsSection._productBenefitsSlideHandler) {
      swiperEl.removeEventListener('swiperslidechange', benefitsSection._productBenefitsSlideHandler);
      swiperEl.dataset.productBenefitsSlideBound = 'false';
    }

    initSection(benefitsSection);
  }

  function onViewportChange() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      section.dataset.productBenefitsInit = 'false';
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', onSectionLoad);

  if (typeof desktopMedia.addEventListener === 'function') {
    desktopMedia.addEventListener('change', onViewportChange);
  } else if (typeof desktopMedia.addListener === 'function') {
    desktopMedia.addListener(onViewportChange);
  }
})();
